import React, { useEffect, useState, useCallback } from 'react';
import { BASE_API } from './Constant';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Flex, Button, Box, Text, Table, Thead, Tbody, Tr, Th, Td, TableContainer, Input } from '@chakra-ui/react';

interface FormDetails {
    [key: string]: string | number;
}

interface ProjectData {
    _id: string;
    datetime_extraction: string;
    extractor_version: number;
    form_details: FormDetails;
    item: Record<string, number>;
    main_project_id: string;
    project_id: number;
    version: number;
    archive_versions?: Record<string, ProjectData>;
}

export interface ApiResponse {
    datetime_extraction: string | number | Date;
    code: number;
    data: {
        current_version?: ProjectData;
        archive_versions?: Record<string, ProjectData>;
    };
    message: string;
}

interface CostSumDataMultiProps {
    projectIds: string[];
}

const CostSumDataMulti: React.FC<CostSumDataMultiProps> = ({ projectIds }) => {
    const [data, setData] = useState<{ [projectId: string]: ProjectData }>({});
    const [editableFormDetails, setEditableFormDetails] = useState<{ [projectId: string]: Partial<FormDetails> }>({});
    const [editableItems, setEditableItems] = useState<{ [projectId: string]: Record<string, number> }>({});

    const fetchData = useCallback(async () => {
        const mainProjectId = sessionStorage.getItem('mainProjectId') || '';
        try {
            const fetchedData: { [projectId: string]: ProjectData } = {};
            await Promise.all(projectIds.map(async projectId => {
                const response = await axios.get(`${BASE_API}/costSummaries/current/${mainProjectId}/data/${projectId}/`);
                const jsonData: ApiResponse = response.data;
                if (jsonData.data.current_version) {
                    fetchedData[projectId] = jsonData.data.current_version;
                }
            }));
            setData(fetchedData);
            setEditableFormDetails(
                Object.fromEntries(Object.entries(fetchedData).map(([projectId, projectData]) => [projectId, projectData.form_details]))
            );
            setEditableItems(
                Object.fromEntries(Object.entries(fetchedData).map(([projectId, projectData]) => [projectId, projectData.item]))
            );
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }, [projectIds]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSave = async () => {
        try {
            await Promise.all(projectIds.map(async projectId => {
                const postData = {
                    ...data[projectId],
                    form_details: editableFormDetails[projectId],
                    item: editableItems[projectId]
                };
                await axios.post(`${BASE_API}/costSummaries/update/`, postData, {
                    headers: { 'Content-Type': 'application/json' },
                });
            }));
            console.log('Dati salvati con successo.');
            fetchData();
        } catch (error) {
            console.error('Errore durante il salvataggio dei dati:', error);
        }
    };

    const handleInputChange = (projectId: string, key: string, value: string, isItem: boolean) => {
        if (isItem) {
            updateEditableItems(projectId, key, value);
        } else {
            updateEditableFormDetails(projectId, key, value);
        }
    };

    const updateEditableItems = (projectId: string, key: string, value: string) => {
        setEditableItems(prevItems => ({
            ...prevItems,
            [projectId]: {
                ...prevItems[projectId],
                [key]: parseFloat(value),
            },
        }));
    };

    const updateEditableFormDetails = (projectId: string, key: string, value: string) => {
        setEditableFormDetails(prevDetails => ({
            ...prevDetails,
            [projectId]: {
                ...prevDetails[projectId],
                [key]: typeof prevDetails[projectId]?.[key] === 'number' ? parseFloat(value) : value,
            },
        }));
    };

    const handleExportToExcel = () => {
        const wb = XLSX.utils.book_new();
        projectIds.forEach(projectId => {
            const formDetailsSheet = XLSX.utils.json_to_sheet([editableFormDetails[projectId]]);
            XLSX.utils.book_append_sheet(wb, formDetailsSheet, `Form_Details_${projectId}`);

            const itemsSheet = XLSX.utils.json_to_sheet(Object.entries(editableItems[projectId]));
            XLSX.utils.book_append_sheet(wb, itemsSheet, `Items_${projectId}`);
        });
        XLSX.writeFile(wb, 'multi_project_data.xlsx');
    };

    return (
        <Box>
            <Flex justifyContent="center" mb={4} gap={4}>
                <Button
                    bg="#414042"
                    color="white"
                    variant="solid"
                    _hover={{ bg: "#E6E7E8", color: "black", boxShadow: "lg", transform: "scale(1.05)" }}
                    _focus={{ boxShadow: "outline" }}
                    onClick={handleExportToExcel}
                >
                    EXPORT ALL
                </Button>
                <Button
                    bg="#414042"
                    color="white"
                    variant="solid"
                    _hover={{ bg: "#E6E7E8", color: "black", boxShadow: "lg", transform: "scale(1.05)" }}
                    _focus={{ boxShadow: "outline" }}
                    onClick={handleSave}
                >
                    SAVE
                </Button>
            </Flex>
            {Object.keys(data).length > 0 ? (
                <>
                    <VersionTable
                        projectIds={projectIds}
                        editableFormDetails={editableFormDetails}
                        handleInputChange={handleInputChange}
                    />
                    <ItemTable
                        projectIds={projectIds}
                        editableItems={editableItems}
                        handleInputChange={handleInputChange}
                    />
                </>
            ) : (
                <Text fontSize="lg" color="#962324" textAlign="center">
                    No data available
                </Text>
            )}
        </Box>
    );
};

interface VersionTableProps {
    projectIds: string[];
    editableFormDetails: { [projectId: string]: Partial<FormDetails> };
    handleInputChange: (projectId: string, key: string, value: string, isItem: boolean) => void;
}

const VersionTable: React.FC<VersionTableProps> = ({
    projectIds,
    editableFormDetails,
    handleInputChange,
}) => (
    <Table style={{ borderCollapse: "collapse", width: "80%", margin: "0 auto" }}>
        <Thead>
            <Tr>
                <Th></Th>
                {projectIds.map(projectId => (
                    <Th key={projectId} style={{ cursor: 'pointer' }}>
                        Project {projectId}
                    </Th>
                ))}
            </Tr>
        </Thead>
        <Tbody>
            {Object.keys(editableFormDetails[projectIds[0]]).map((key) => (
                <Tr key={key}>
                    <Td>{key}</Td>
                    {projectIds.map((projectId) => (
                        <Td key={projectId}>
                            <Input
                                type={typeof editableFormDetails[projectId]?.[key] === 'number' ? 'number' : 'text'}
                                value={editableFormDetails[projectId]?.[key] || ''}
                                onChange={(e) => handleInputChange(projectId, key, e.target.value, false)}
                            />
                        </Td>
                    ))}
                </Tr>
            ))}
        </Tbody>
    </Table>
);

interface ItemTableProps {
    projectIds: string[];
    editableItems: { [projectId: string]: Record<string, number> };
    handleInputChange: (projectId: string, key: string, value: string, isItem: boolean) => void;
}

const ItemTable: React.FC<ItemTableProps> = ({
    projectIds,
    editableItems,
    handleInputChange,
}) => (
    <Table style={{ borderCollapse: "collapse", width: "80%", margin: "0 auto" }}>
        <Thead>
            <Tr>
                <Th></Th>
                {projectIds.map(projectId => (
                    <Th key={projectId} style={{ cursor: 'pointer' }}>
                        Project {projectId}
                    </Th>
                ))}
            </Tr>
        </Thead>
        <Tbody>
            {Object.keys(editableItems[projectIds[0]]).map((key) => (
                <Tr key={key}>
                    <Td>{key}</Td>
                    {projectIds.map((projectId) => (
                        <Td key={projectId}>
                            <Input
                                type="number"
                                value={editableItems[projectId]?.[key] || ''}
                                onChange={(e) => handleInputChange(projectId, key, e.target.value, true)}
                            />
                        </Td>
                    ))}
                </Tr>
            ))}
        </Tbody>
    </Table>
);

export default CostSumDataMulti;
